import React, { useState, useEffect, useCallback } from 'react';
import './AdminContactUs.css';
import { fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';

const emptyContact = () => ({ title: '', name: '', email: '' });

const AdminContactUs = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [shared, setShared] = useState({ mailing_address_line1: '', mailing_address_line2: '', facebook_url: '' });
  const [savingShared, setSavingShared] = useState(false);

  const [branches, setBranches] = useState([]); // [{branch_id, branch_name, contacts: [...]}]
  const [savingBranch, setSavingBranch] = useState(null); // branch_id currently saving

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/contact-us`);
      const data = await res.json();
      if (res.ok) {
        setShared({
          mailing_address_line1: data.mailing_address_line1 || '',
          mailing_address_line2: data.mailing_address_line2 || '',
          facebook_url: data.facebook_url || '',
        });
        setBranches(data.branches || []);
      } else {
        setError(data.error || 'Failed to load contact info.');
      }
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveShared = async () => {
    setSavingShared(true);
    setError('');
    setMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/contact-us/shared`, {
        method: 'PUT',
        body: JSON.stringify(shared),
      });
      const data = await res.json();
      if (res.ok && data.success) setMessage('Mailing address and Facebook link saved.');
      else setError(data.error || 'Failed to save.');
    } catch {
      setError('Network error.');
    }
    setSavingShared(false);
  };

  const updateContact = (branchId, index, field, value) => {
    setBranches(prev => prev.map(b => b.branch_id !== branchId ? b : {
      ...b,
      contacts: b.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c),
    }));
  };

  const addContact = (branchId) => {
    setBranches(prev => prev.map(b => b.branch_id !== branchId ? b : {
      ...b, contacts: [...b.contacts, emptyContact()],
    }));
  };

  const removeContact = (branchId, index) => {
    setBranches(prev => prev.map(b => b.branch_id !== branchId ? b : {
      ...b, contacts: b.contacts.filter((_, i) => i !== index),
    }));
  };

  const moveContact = (branchId, index, direction) => {
    setBranches(prev => prev.map(b => {
      if (b.branch_id !== branchId) return b;
      const swapWith = direction === 'up' ? index - 1 : index + 1;
      if (swapWith < 0 || swapWith >= b.contacts.length) return b;
      const contacts = [...b.contacts];
      [contacts[index], contacts[swapWith]] = [contacts[swapWith], contacts[index]];
      return { ...b, contacts };
    }));
  };

  const saveBranch = async (branch) => {
    for (const c of branch.contacts) {
      if (!c.title.trim() || !c.email.trim()) {
        setError(`${branch.branch_name}: every contact needs at least a title and an email.`);
        return;
      }
    }
    setSavingBranch(branch.branch_id);
    setError('');
    setMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/contact-us/branch/${branch.branch_id}`, {
        method: 'PUT',
        body: JSON.stringify({ contacts: branch.contacts }),
      });
      const data = await res.json();
      if (res.ok && data.success) setMessage(`${branch.branch_name} contacts saved.`);
      else setError(data.error || 'Failed to save.');
    } catch {
      setError('Network error.');
    }
    setSavingBranch(null);
  };

  if (loading) return <div className="contact-us-admin-container"><p>Loading…</p></div>;

  return (
    <div className="contact-us-admin-container">
      <h2>Contact Us Page</h2>
      <p className="contact-us-admin-subtitle">
        Mailing address and the Facebook link are shared across the whole site. Each branch
        manages its own ordered list of contacts independently -- branches don't need matching
        roles or the same number of rows.
      </p>

      {error && <div className="error-banner">{error}</div>}
      {message && <p className="contact-us-admin-success-msg">{message}</p>}

      <div className="form-card">
        <h3>Shared info</h3>
        <div className="form-row">
          <label>Mailing address line 1</label>
          <input
            type="text"
            value={shared.mailing_address_line1}
            onChange={e => setShared(s => ({ ...s, mailing_address_line1: e.target.value }))}
            placeholder="e.g. PO BOX 1581"
          />
        </div>
        <div className="form-row">
          <label>Mailing address line 2</label>
          <input
            type="text"
            value={shared.mailing_address_line2}
            onChange={e => setShared(s => ({ ...s, mailing_address_line2: e.target.value }))}
            placeholder="e.g. Cedar Park, TX 78630"
          />
        </div>
        <div className="form-row">
          <label>Facebook URL</label>
          <input
            type="text"
            value={shared.facebook_url}
            onChange={e => setShared(s => ({ ...s, facebook_url: e.target.value }))}
            placeholder="https://www.facebook.com/..."
          />
        </div>
        <button className="btn-primary" onClick={saveShared} disabled={savingShared}>
          {savingShared ? 'Saving…' : 'Save shared info'}
        </button>
      </div>

      {branches.map(branch => (
        <div className="form-card" key={branch.branch_id}>
          <h3>{branch.branch_name}</h3>

          {branch.contacts.length === 0 ? (
            <p className="muted">No contacts yet for this branch.</p>
          ) : (
            <div className="contact-rows">
              {branch.contacts.map((c, i) => (
                <div className="contact-row" key={i}>
                  <div className="contact-row-move">
                    <button
                      className="btn-sm"
                      onClick={() => moveContact(branch.branch_id, i, 'up')}
                      disabled={i === 0}
                      title="Move up"
                    >▲</button>
                    <button
                      className="btn-sm"
                      onClick={() => moveContact(branch.branch_id, i, 'down')}
                      disabled={i === branch.contacts.length - 1}
                      title="Move down"
                    >▼</button>
                  </div>
                  <input
                    type="text" placeholder="Title (e.g. Principal)"
                    value={c.title}
                    onChange={e => updateContact(branch.branch_id, i, 'title', e.target.value)}
                  />
                  <input
                    type="text" placeholder="Name (optional)"
                    value={c.name}
                    onChange={e => updateContact(branch.branch_id, i, 'name', e.target.value)}
                  />
                  <input
                    type="text" placeholder="Email"
                    value={c.email}
                    onChange={e => updateContact(branch.branch_id, i, 'email', e.target.value)}
                  />
                  <button className="btn-sm btn-danger" onClick={() => removeContact(branch.branch_id, i)}>Remove</button>
                </div>
              ))}
            </div>
          )}

          <div className="form-actions">
            <button className="btn-secondary" onClick={() => addContact(branch.branch_id)}>+ Add contact</button>
            <button className="btn-primary" onClick={() => saveBranch(branch)} disabled={savingBranch === branch.branch_id}>
              {savingBranch === branch.branch_id ? 'Saving…' : `Save ${branch.branch_name}`}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminContactUs;
